import { LocalizationKeys } from '../i18n'
import type { TranslateFunction } from '../contexts/localizationContext'

/** Time constants in milliseconds */
const MS_PER_MINUTE = 60_000
const MS_PER_HOUR = 3_600_000
const MS_PER_DAY = 86_400_000
const MS_PER_WEEK = 604_800_000
const MS_PER_MONTH = 2_592_000_000 // ~30 days
const MS_PER_YEAR = 31_536_000_000 // ~365 days

/**
 * Форматирует дату создания уведомления в читаемый формат
 * @param dateString - ISO строка даты
 * @param t - функция перевода из контекста локализации
 * @returns Форматированная строка времени
 */
export function formatNotificationDate(
  dateString: string,
  t: TranslateFunction
): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / MS_PER_MINUTE)
  const diffHours = Math.floor(diffMs / MS_PER_HOUR)
  const diffDays = Math.floor(diffMs / MS_PER_DAY)
  const diffWeeks = Math.floor(diffMs / MS_PER_WEEK)
  const diffMonths = Math.floor(diffMs / MS_PER_MONTH)
  const diffYears = Math.floor(diffMs / MS_PER_YEAR)

  // Только что (меньше минуты)
  if (diffMins < 1) {
    return t(LocalizationKeys.NOTIFICATION_JUST_NOW)
  }

  // Минуты (1-59 минут)
  if (diffMins < 60) {
    return t(LocalizationKeys.NOTIFICATION_MINUTES_AGO, diffMins )
  }

  // Часы (1-23 часа)
  if (diffHours < 24) {
    return t(LocalizationKeys.NOTIFICATION_HOURS_AGO, diffHours)
  }

  // Дни (1-6 дней)
  if (diffDays < 7) {
    return t(LocalizationKeys.NOTIFICATION_DAYS_AGO, diffDays)
  }

  // Недели (1-3 недели)
  if (diffWeeks < 4) {
    return t(LocalizationKeys.NOTIFICATION_WEEKS_AGO, diffWeeks)
  }

  // Месяцы (1-11 месяцев)
  if (diffMonths < 12) {
    return t(LocalizationKeys.NOTIFICATION_MONTHS_AGO, diffMonths)
  }

  // Годы (1+ лет)
  return t(LocalizationKeys.NOTIFICATION_YEARS_AGO, diffYears)
}

/**
 * Получает текст количества непрочитанных уведомлений
 * @param count - количество уведомлений
 * @param t - функция перевода из контекста локализации
 * @returns Форматированная строка с количеством
 */
export function getUnreadNotificationText(
  count: number,
  t: TranslateFunction
): string {
  if (count === 0) {
    return t(LocalizationKeys.NOTIFICATION_NO_NEW)
  }

  // Для русского языка нужно учитывать множественное число
  // 1 уведомление, 2-4 уведомления, 5+ уведомлений
  let translationKey = LocalizationKeys.NOTIFICATION_UNREAD_COUNT as string
  
  if (count > 1) {
    // Для русского: 2-4 используем _2, для остальных _plural
    const lastDigit = count % 10
    const lastTwoDigits = count % 100
    
    if (lastDigit >= 2 && lastDigit <= 4 && (lastTwoDigits < 10 || lastTwoDigits >= 20)) {
      translationKey = `${LocalizationKeys.NOTIFICATION_UNREAD_COUNT}_2`
    } else {
      translationKey = `${LocalizationKeys.NOTIFICATION_UNREAD_COUNT}_plural`
    }
  }

  return t(translationKey, count)
}
